import { useEffect, useRef } from 'react'

import EnterpriseAppFrameHeader from 'src/features/navigation/components/enterprise-app-frame-header'
import { NotificationModalsProvider } from 'src/features/notifications/context/notification-modals-context'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesEnterprise } from 'src/shared/utils/tailwind-styles'

import {
  useAppWindowFlowShellStore,
  useAppWindowInsetsStore,
  useAppWindowScrollStore,
} from './app-window-frame-business'

export {
  useAppWindowInsetsStore,
  useAppWindowScrollStore,
  useAppWindowFlowShellStore,
} from './app-window-frame-business'

interface AppWindowFrameEnterpriseProps {
  children: React.ReactNode
}

export default function AppWindowFrameEnterprise({ children }: AppWindowFrameEnterpriseProps) {
  const { setInsets } = useAppWindowInsetsStore()
  const { setAppWindowScrollContainer } = useAppWindowScrollStore()
  const { leftPanelContent, leftPanelWidth, headerHeight } = useAppWindowFlowShellStore()
  const { isSmTabletOrSmaller, breakpoint } = useBreakpoint()

  const contentRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // const sidebarRef = useRef<HTMLDivElement>(null)

  // const [isSidebarHovered, setIsSidebarHovered] = useState(false)
  const isFlowMode = Boolean(leftPanelContent)
  const leftPanelTargetWidth = isFlowMode ? leftPanelWidth : 0

  // useEffect(() => {
  //   const sidebar = sidebarRef.current
  //   if (!sidebar) {
  //     return
  //   }
  //   const handleMouseEnter = () => {
  //     setIsSidebarHovered(true)
  //   }
  //   const handleMouseLeave = () => {
  //     setIsSidebarHovered(false)
  //   }
  //   sidebar.addEventListener('mouseenter', handleMouseEnter)
  //   sidebar.addEventListener('mouseleave', handleMouseLeave)
  //   return () => {
  //     sidebar.removeEventListener('mouseenter', handleMouseEnter)
  //     sidebar.removeEventListener('mouseleave', handleMouseLeave)
  //   }
  // }, [isSmTabletOrSmaller])

  useEffect(() => {
    if (contentRef.current) {
      const x = contentRef.current.getBoundingClientRect().x
      const y = contentRef.current.getBoundingClientRect().y
      const width = contentRef.current.getBoundingClientRect().width
      const height = contentRef.current.getBoundingClientRect().height

      setInsets({
        top: Math.ceil(y),
        left: Math.ceil(x),
        bottom: Math.ceil(window.innerHeight - height - y),
        right: Math.ceil(window.innerWidth - width - x),
      })
    }
  }, [isSmTabletOrSmaller, breakpoint, setInsets, headerHeight, leftPanelTargetWidth])

  useEffect(() => {
    if (!contentRef.current) {
      return
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const x = entry.target.getBoundingClientRect().x
        const y = entry.target.getBoundingClientRect().y
        const width = entry.target.getBoundingClientRect().width
        const height = entry.target.getBoundingClientRect().height

        setInsets({
          top: Math.ceil(y),
          left: Math.ceil(x),
          bottom: Math.ceil(window.innerHeight - height - y),
          right: Math.ceil(window.innerWidth - width - x),
        })
      }
    })
    observer.observe(contentRef.current)
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [contentRef, setInsets])

  useEffect(() => {
    setAppWindowScrollContainer(scrollContainerRef.current)
    return () => setAppWindowScrollContainer(null)
  }, [setAppWindowScrollContainer])

  return (
    <NotificationModalsProvider>
      <main
        className={cn(
          'flex h-screen w-full flex-col overflow-hidden bg-primary px-[10px] pb-[17px]',
          ScrollBarTailwindClassesEnterprise,
        )}
      >
        <div
          className={cn('transition-[height] duration-300', isFlowMode ? 'overflow-hidden' : 'overflow-visible')}
          style={{ height: headerHeight }}
        >
          <div className={cn('transition-opacity duration-200', isFlowMode && 'pointer-events-none opacity-0')}>
            <EnterpriseAppFrameHeader />
          </div>
        </div>
        <div className="flex w-full flex-1 overflow-hidden">
          <div
            className="overflow-hidden transition-[width,opacity,transform] duration-300 ease-out"
            style={{
              width: leftPanelTargetWidth,
              opacity: leftPanelTargetWidth > 0 ? 1 : 0,
              transform: `translateX(${leftPanelTargetWidth > 0 ? 0 : -16}px)`,
            }}
          >
            <div className="size-full bg-primary pr-4">{leftPanelContent}</div>
          </div>
          <div className="min-w-0 flex-1 overflow-hidden rounded-[25px] bg-white" ref={contentRef}>
            <div ref={scrollContainerRef} className="size-full flex-1 overflow-y-auto">
              {children}
            </div>
          </div>
        </div>
      </main>
    </NotificationModalsProvider>
  )
}
